import { cloneElement, isValidElement, type ReactElement } from "react";
import type { CanProps, CannotProps } from "./types";
import { useCan } from "./hooks";

/**
 * Componente para renderização condicional baseada em permissões
 * Renderiza children apenas se usuário TEM a permissão
 */
export function Can<TContext = any>({
  action,
  resource,
  context,
  children,
  fallback = null,
  passThrough = false,
}: CanProps<TContext>) {
  const allowed = useCan(action, resource, context);

  // Se tem permissão, renderiza children normalmente
  if (allowed) {
    return <>{children}</>;
  }

  // Se passThrough está ativo, renderiza children com prop adicional
  if (passThrough && isValidElement(children)) {
    return cloneElement(children as React.ReactElement<any>, {
      disabled: true,
      "data-permission-denied": true,
      "aria-disabled": true,
    });
  }

  // Sem permissão, renderiza fallback
  return <>{fallback}</>;
}

/**
 * Componente inverso - renderiza apenas se usuário NÃO tem permissão
 */
export function Cannot<TContext = any>({
  action,
  resource,
  context,
  children,
  passThrough = false,
}: CannotProps<TContext>) {
  const allowed = useCan(action, resource, context);

  // Se tem permissão, não renderiza nada
  if (allowed && !passThrough) {
    return null;
  }

  // Se tem permissão mas passThrough está ativo
  if (allowed && passThrough && isValidElement(children)) {
    return cloneElement(children as React.ReactElement<any>, {
      disabled: false,
      "data-permission-granted": true,
      "aria-disabled": false,
    });
  }

  // Não tem permissão, renderiza children
  return <>{children}</>;
}

/**
 * Componente para renderizar diferentes conteúdos baseado em permissão
 */
export interface IfCanProps<TContext = any> {
  action: string;
  resource?: string;
  context?: TContext;
  yes: React.ReactNode;
  no?: React.ReactNode;
}

export function IfCan<TContext = any>({
  action,
  resource,
  context,
  yes,
  no = null,
}: IfCanProps<TContext>) {
  const allowed = useCan(action, resource, context);
  return <>{allowed ? yes : no}</>;
}

/**
 * Componente para verificar múltiplas permissões (ANY)
 */
export interface CanAnyProps<TContext = any> {
  permissions: Array<{
    action: string;
    resource?: string;
    context?: TContext;
  }>;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function CanAny<TContext = any>({
  permissions,
  children,
  fallback = null,
}: CanAnyProps<TContext>) {
  const canAny = permissions.some((perm) =>
    useCan(perm.action, perm.resource, perm.context),
  );

  return <>{canAny ? children : fallback}</>;
}

/**
 * Componente para verificar múltiplas permissões (ALL)
 */
export interface CanAllProps<TContext = any> {
  permissions: Array<{
    action: string;
    resource?: string;
    context?: TContext;
  }>;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function CanAll<TContext = any>({
  permissions,
  children,
  fallback = null,
}: CanAllProps<TContext>) {
  const canAll = permissions.every((perm) =>
    useCan(perm.action, perm.resource, perm.context),
  );

  return <>{canAll ? children : fallback}</>;
}

/**
 * HOC (Higher Order Component) para adicionar verificação de permissão
 */
export function withCan<P extends object, TContext = any>(
  Component: React.ComponentType<P>,
  action: string,
  resource?: string,
  options?: {
    fallback?: React.ComponentType<P>;
    redirectTo?: string;
    onDenied?: () => void;
  },
) {
  return function WithCanComponent(props: P & { context?: TContext }) {
    const allowed = useCan(action, resource, props.context);

    if (!allowed) {
      // Callback quando negado
      options?.onDenied?.();

      // Se tem componente fallback
      if (options?.fallback) {
        const FallbackComponent = options.fallback;
        return <FallbackComponent {...props} />;
      }

      // Se deve redirecionar (implementação depende do roteador usado)
      if (options?.redirectTo && typeof window !== "undefined") {
        // Exemplo com window.location (adaptar para React Router se necessário)
        window.location.href = options.redirectTo;
      }

      return null;
    }

    return <Component {...props} />;
  };
}

/**
 * Componente para aplicar classes CSS baseado em permissões
 */
export interface PermissionClassProps<TContext = any> {
  action: string;
  resource?: string;
  context?: TContext;
  className: string;
  deniedClassName?: string;
  children: React.ReactElement;
}

export function PermissionClass<TContext = any>({
  action,
  resource,
  context,
  className,
  deniedClassName = "",
  children,
}: PermissionClassProps<TContext>) {
  const allowed = useCan(action, resource, context);

  if (!isValidElement(children)) {
    return null;
  }

  const appliedClassName = allowed ? className : deniedClassName;
  const existingClassName =
    (children as ReactElement<{ className?: string }>).props.className || "";

  return cloneElement(children as ReactElement<any>, {
    className: `${existingClassName} ${appliedClassName}`.trim(),
    "data-permission": allowed ? "granted" : "denied",
  });
}
